import { corelCommunication } from "../../../lib/corel/communication";
import { SessionService } from "../../../lib/corel/actions/session";
import { VBCompilerService } from "../../../lib/corel/actions/vbcompiler";
import type { ToolData } from "../../../lib/corel/actions/vbcompiler.types";
import { CorelWorks } from "../../../lib/corel/corelworks";
import { sendToTerminal } from "../../../lib/terminalLogger";
import {
	logSessionState,
	logToolResult,
} from "../../../lib/corel/logging/toolLogger";
import { OneAgruparToolData } from "../types/tools";
import { api, APIError } from "../../../lib/api";
import {
	ensureFreshSsoToken,
	getBaseBearer,
	fetchWithSsoRetry,
} from "../../../lib/toolsSso";
import { registerExecution } from "../../../lib/registerExecution";
import { API_URL } from "../../../config";
import { handleToolsApiError } from "../../../lib/toolsErrorHandler";
import { apiWithRetry } from "../../../lib/apiRetry";

// Timeout específico para vbcompiler com operações demoradas (60 minutos)
const VBCOMPILER_TIMEOUT_MS = 60 * 60 * 1000; // 60 minutos

// ✅ ATUALIZADO: Usando SessionService centralizado
async function ensureSessionTokenLocal(
	timeoutMs: number = 3000,
): Promise<boolean> {
	const result = await SessionService.ensureToken({ timeoutMs });
	return result.success;
}

export const corelToolsService = {
	ensureSessionTokenLocal,

	async runPhotoshopExternalEdit() {
		await ensureSessionTokenLocal(3000);
		logSessionState("RUNPS");

		const resp = await CorelWorks.executeExternalEditPhotoshop(
			{
				format: "TIFF",
				dpi: 300,
				embedICC: true,
				compression: "LZW",
				timeoutMs: 900000,
				debounceMs: 1000,
				replaceInDoc: true,
			} as any,
			"Selection",
		);

		logToolResult("RUNPS", resp);
		return resp;
	},

	async collectMetadata(): Promise<any> {
		await ensureSessionTokenLocal(3000);
		logSessionState("METADADOS");

		const resp = await new Promise<any>((resolve) => {
			try {
				corelCommunication.sendActionGuarded(
					"coletar-metadados",
					{},
					(r: any) => resolve(r),
				);
			} catch (e) {
				resolve({ status: "error", error: String(e) });
			}
		});
		logToolResult("METADADOS", resp);
		return resp;
	},

	async smartImport(): Promise<any> {
		await ensureSessionTokenLocal(3000);
		logSessionState("SMART_IMPORT");

		const resp = await new Promise<any>((resolve) => {
			try {
				corelCommunication.sendActionGuarded(
					"abrir-importacao-inteligente",
					{},
					(r: any) => resolve(r),
				);
			} catch (e) {
				resolve({ status: "error", error: String(e) });
			}
		});
		logToolResult("SMART_IMPORT", resp);
		return resp;
	},

	async openFreeFile(): Promise<any> {
		await ensureSessionTokenLocal(3000);
		logSessionState("OPEN_FREE_FILE");

		const resp = await new Promise<any>((resolve) => {
			try {
				corelCommunication.sendActionGuarded(
					"abrir-arquivo-livre",
					{},
					(r: any) => resolve(r),
				);
			} catch (e) {
				resolve({ status: "error", error: String(e) });
			}
		});
		logToolResult("OPEN_FREE_FILE", resp);
		return resp;
	},

	async exportBatchLocal(): Promise<any> {
		await ensureSessionTokenLocal(3000);
		logSessionState("EXPORT_BATCH_LOCAL");

		const resp = await new Promise<any>((resolve) => {
			try {
				corelCommunication.sendActionGuarded(
					"corel-export-batch",
					{ mode: "local" },
					(r: any) => resolve(r),
				);
			} catch (e) {
				resolve({ status: "error", error: String(e) });
			}
		});

		logToolResult("EXPORT_BATCH_LOCAL", resp);
		return resp;
	},

	async exportProducaoOffline(data: Record<string, unknown>): Promise<any> {
		await ensureSessionTokenLocal(3000);
		logSessionState("EXPORT_PRODUCAO_OFFLINE");

		const resp = await new Promise<any>((resolve) => {
			try {
				corelCommunication.sendActionGuarded(
					"corel-export-batch",
					data,
					(r: any) => resolve(r),
				);
			} catch (e) {
				resolve({ status: "error", error: String(e) });
			}
		});

		logToolResult("EXPORT_PRODUCAO_OFFLINE", resp);
		return resp;
	},

	async repairPdf(qpdfDirectory: string): Promise<any> {
		await ensureSessionTokenLocal(3000);
		logSessionState("REPAIR_PDF");

		const resp = await new Promise<any>((resolve) => {
			try {
				corelCommunication.sendActionGuarded(
					"repair-pdf-and-open",
					{ qpdfDirectory },
					(r: any) => resolve(r),
				);
			} catch (e) {
				resolve({ status: "error", error: String(e) });
			}
		});

		logToolResult("REPAIR_PDF", resp);
		return resp;
	},

	async captureIndividualObjects(options?: {
		dpi?: number;
		escala?: number;
		maxPixels?: number;
		maxBytes?: number;
	}): Promise<any> {
		await ensureSessionTokenLocal(3000);
		logSessionState("CAPTURE_IMAGES");

		const data = {
			...(options?.dpi !== undefined && { dpi: options.dpi }),
			...(options?.escala !== undefined && { escala: options.escala }),
			...(options?.maxPixels !== undefined && { maxPixels: options.maxPixels }),
			...(options?.maxBytes !== undefined && { maxBytes: options.maxBytes }),
		};

		// Seguindo o padrão de collectMetadata() e smartImport()
		const resp = await new Promise<any>((resolve) => {
			try {
				corelCommunication.sendActionGuarded(
					"capturar-objetos-individuais",
					data,
					(r: any) => resolve(r),
				);
			} catch (e) {
				resolve({ status: "error", error: String(e) });
			}
		});

		logToolResult("CAPTURE_IMAGES", resp);
		return resp;
	},

	async closeDocument(): Promise<any> {
		await ensureSessionTokenLocal(3000);
		logSessionState("CLOSE_DOCUMENT");

		// Request ID é gerado automaticamente pelo CorelCommunication
		// Payload deve ser vazio conforme documentação
		const resp = await new Promise<any>((resolve) => {
			try {
				sendToTerminal(
					"📤 [CLOSE_DOCUMENT] Enviando action (payload vazio)",
					{},
				);
				corelCommunication.sendActionGuarded("fechar-documento", {}, (r: any) =>
					resolve(r),
				);
			} catch (e) {
				resolve({ status: "error", error: String(e) });
			}
		});

		logToolResult("CLOSE_DOCUMENT", resp);
		return resp;
	},

	async oneAgruparExecute(tool: OneAgruparToolData) {
		await ensureSessionTokenLocal(3000);
		logSessionState("ONEAGRUPAR");

		// Validar estrutura do tool antes de usar
		if (!tool || typeof tool !== "object") {
			throw new Error("Dados da ferramenta inválidos: tool não é um objeto");
		}

		// Validar código
		if (!tool.code || typeof tool.code !== "string") {
			throw new Error("Código da ferramenta ausente ou inválido");
		}

		const code = tool.code.trim();
		if (code.length === 0) {
			throw new Error("Código da ferramenta está vazio");
		}

		// Validar defaults
		if (!tool.defaults || typeof tool.defaults !== "object") {
			throw new Error(
				"Configurações padrão da ferramenta ausentes ou inválidas",
			);
		}

		// Validar campos dos defaults
		if (typeof tool.defaults.marginTolerancia !== "number") {
			throw new Error("marginTolerancia deve ser um número");
		}
		if (typeof tool.defaults.prefixoNomeGrupo !== "string") {
			throw new Error("prefixoNomeGrupo deve ser uma string");
		}
		if (typeof tool.defaults.usarNomenclaturaNumerica !== "boolean") {
			throw new Error("usarNomenclaturaNumerica deve ser um booleano");
		}

		const parameters = {
			marginTolerancia: tool.defaults.marginTolerancia,
			prefixoNomeGrupo: tool.defaults.prefixoNomeGrupo,
			usarNomenclaturaNumerica: tool.defaults.usarNomenclaturaNumerica,
		} as const;

		// ✅ ATUALIZADO: Usando VBCompilerService centralizado
		const vbid =
			tool.config?.vbid || String(tool.slug || "agrupar-objetos-sobrepostos");
		const result = await VBCompilerService.executeWithCode(
			vbid,
			code,
			parameters,
			{
				progress: tool.config?.progress ?? true,
				progressEvery: tool.config?.progressEvery ?? 3,
				progressIntervalMs: tool.config?.progressIntervalMs ?? 150,
				progressLabel: tool.config?.progressLabel ?? "Agrupamento/Objetos",
				progressStartEnd: tool.config?.progressStartEnd ?? true,
				uiModal: tool.config?.uiModal ?? true,
				uiCancelable: tool.config?.uiCancelable ?? true,
				disablePerformanceMonitor:
					tool.config?.disablePerformanceMonitor ?? false,
			},
		);

		logToolResult("ONEAGRUPAR", result.response);

		// Registrar execução na API
		if (result.requestId) {
			try {
				registerExecution(
					{
						requestId: result.requestId,
						status: result.status,
						message: result.response?.message,
						result: result.response?.result,
					},
					tool,
					parameters,
				).catch((err) => {
					sendToTerminal("⚠️ [ONEAGRUPAR] Erro ao registrar execução", {
						error: String(err),
					});
				});
			} catch {}
		}

		return result.response;
	},

	async runCommandById(id: number) {
		await ensureSessionTokenLocal(3000);
		logSessionState("COMMAND");

		const endpoint = `/tools/commands/${id}`;
		const fullUrl = `${API_URL}${endpoint}`;

		sendToTerminal("🔍 [COMMAND] Iniciando busca de comando", {
			id,
			endpoint,
			fullUrl,
		});

		// ✅ REMOVIDO: ensureFreshSsoToken() duplicado - fetchWithSsoRetry() já faz isso internamente
		// Tentar buscar payload com retry em caso de 401
		let payload;
		try {
			// Usar fetchWithSsoRetry para implementar estratégia oficial
			sendToTerminal("📡 [COMMAND] Fazendo requisição GET", {
				id,
				fullUrl,
			});

			const res = await fetchWithSsoRetry(
				fullUrl,
				{
					method: "GET",
					headers: { "Content-Type": "application/json" },
				},
				getBaseBearer(),
			);

			sendToTerminal("📥 [COMMAND] Resposta recebida", {
				id,
				status: res.status,
				statusText: res.statusText,
				ok: res.ok,
			});

			if (!res.ok) {
				const errorText = await res.text().catch(() => "Erro desconhecido");
				sendToTerminal("❌ [COMMAND] Resposta não OK", {
					id,
					status: res.status,
					statusText: res.statusText,
					errorText,
				});
				throw new Error(`HTTP ${res.status}: ${res.statusText}`);
			}

			payload = await res.json();
			sendToTerminal("✅ [COMMAND] Payload recebido", {
				id,
				hasAction: !!payload?.action,
				hasData: !!payload?.data,
				action: payload?.action,
			});
		} catch (error: any) {
			// ✅ MIGRADO: Usar handleToolsApiError para melhor tratamento
			let errorMessage = `Falha na API: ${error.message}`;

			if (error instanceof APIError || (error?.status && error?.message)) {
				const apiError =
					error instanceof APIError
						? error
						: new APIError(error.status, error.message, "http", error.data);

				// Criar novo APIError com metadata enriquecido (evitar mutação)
				const enhancedError = new APIError(
					apiError.status,
					apiError.message,
					apiError.kind,
					{ ...apiError.data, url: fullUrl, endpoint },
				);

				// Converter para AppError usando handler
				const appError = handleToolsApiError(enhancedError, "command");
				errorMessage = appError.message;
			}

			sendToTerminal("❌ [COMMAND] Erro ao buscar comando", {
				id,
				endpoint,
				fullUrl,
				error: error instanceof Error ? error.message : String(error),
				errorStatus: error?.status,
			});
			return {
				status: "error",
				message: errorMessage,
			} as const;
		}

		// payload esperado: { action: string, data: any }
		const action = payload?.action;
		const data = payload?.data ?? {};
		if (!action) {
			sendToTerminal("❌ [COMMAND] Payload inválido", { id, payload });
			return { status: "error", message: "Payload inválido da API" } as const;
		}
		return await new Promise<any>((resolve) => {
			try {
				// ✅ Diagnóstico pré-envio
				const diagnostics = corelCommunication.getDiagnostics();
				const hasWebView = diagnostics.hasWebView;
				const hasToken = !!diagnostics.token;
				const hasHmac = diagnostics.hasHmacSecret;
				const tokenValid = diagnostics.tokenValid;
				const queueSize = diagnostics.queueSize;

				sendToTerminal("🔍 [COMMAND] Diagnóstico pré-envio", {
					id,
					action,
					hasWebView,
					hasToken,
					hasHmac,
					tokenValid,
					queueSize,
					tokenLength: diagnostics.token?.length ?? 0,
					recommendations: diagnostics.recommendations,
				});

				const requestId = corelCommunication.sendActionGuarded(
					action,
					data,
					(resp: any) => {
						if (resp?.status === "error") {
							sendToTerminal("❌ [COMMAND] Erro na resposta", {
								id,
								action,
								requestId,
								status: resp?.status,
								message: resp?.message,
								errorCode: resp?.errorCode,
								errorDetails: resp?.errorDetails,
							});
						}
						resolve(resp);
					},
				);

				// ✅ Diagnóstico pós-envio (requestId nunca deve ser null agora)
				if (!requestId) {
					const postDiagnostics = corelCommunication.getDiagnostics();
					sendToTerminal("❌ [COMMAND] Falha ao enviar - requestId é null", {
						id,
						action,
						preDiagnostics: {
							hasWebView,
							hasToken,
							hasHmac,
							tokenValid,
							queueSize,
						},
						postDiagnostics: {
							hasWebView: postDiagnostics.hasWebView,
							hasToken: !!postDiagnostics.token,
							hasHmac: postDiagnostics.hasHmacSecret,
							tokenValid: postDiagnostics.tokenValid,
							queueSize: postDiagnostics.queueSize,
							recommendations: postDiagnostics.recommendations,
						},
						windowSessionToken: !!(window as any)?.sessionToken,
						windowSessionTokenLength:
							(window as any)?.sessionToken?.length ?? 0,
						windowHmac: !!(window as any)?.sessionCrypto?.hmacKeyB64,
						windowHmacLength:
							(window as any)?.sessionCrypto?.hmacKeyB64?.length ?? 0,
						windowDockerId: (window as any)?.dockerId || "ausente",
					});

					resolve({
						status: "error",
						message:
							"Falha ao enviar comando (sessão ausente ou WebView indisponível)",
						errorCode: "SEND_FAILED",
						errorDetails: {
							hasWebView,
							hasToken,
							hasHmac,
							tokenValid,
							recommendations: postDiagnostics.recommendations,
						},
					});
					return;
				}

				sendToTerminal("✅ [COMMAND] Comando enviado com sucesso", {
					id,
					action,
					requestId,
					wasEnqueued: queueSize > 0,
				});

				// Fallback: timeout de segurança (120s) caso o host não responda
				const timeoutMs = 300_000;
				try {
					let settled = false;
					const originalResolve = resolve;
					const toId = window.setTimeout(() => {
						if (settled) return;
						settled = true;
						sendToTerminal("⚠️ [COMMAND] Timeout excedido", {
							id,
							action,
							requestId,
							timeoutMs,
						});
						originalResolve({
							status: "error",
							message: "Tempo limite do comando excedido",
						});
					}, timeoutMs);
					resolve = (value: any) => {
						if (settled) return;
						settled = true;
						try {
							window.clearTimeout(toId);
						} catch {}
						originalResolve(value);
					};
				} catch {}
			} catch (e) {
				sendToTerminal("❌ [COMMAND] Exceção", {
					id,
					action,
					error: String(e),
				});
				resolve({ status: "error", error: String(e) });
			}
		});
	},
};
